import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Trash2, Search, XCircle } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useStudents, Student } from '../../contexts/StudentContext';
import fipsData from '../../data/fips.json';

interface County {
  state: string;
  county: string;
}

interface DFPBOrderFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Cast fips data to proper type
const typedFipsData = fipsData as Record<string, { STATE: string; COUNTY: string }>;

// Get unique states from FIPS data
const US_STATES = Array.from(new Set(Object.values(typedFipsData).map(record => record.STATE))).sort();

export function DFPBOrderForm({ isOpen, onClose, onSuccess }: DFPBOrderFormProps) {
  const { dfpbStudents: students } = useStudents();
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [counties, setCounties] = useState<County[]>([{ state: '', county: '' }]);
  const [orderType, setOrderType] = useState<'new_list' | 'additional_records'>('new_list');
  const [additionalRecordsCount, setAdditionalRecordsCount] = useState('');
  const [additionalRecordsReason, setAdditionalRecordsReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.student-dropdown-container')) {
        setShowStudentDropdown(false);
      }
    };

    if (showStudentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStudentDropdown]);

  const addCounty = () => {
    if (counties.length < 3) {
      setCounties([...counties, { state: '', county: '' }]);
    }
  };

  const removeCounty = (index: number) => {
    if (counties.length > 1) {
      setCounties(counties.filter((_, i) => i !== index));
    }
  };

  const updateCounty = (index: number, field: 'state' | 'county', value: string) => {
    const updated = [...counties];
    updated[index][field] = value;

    // Clear county when state changes
    if (field === 'state') {
      updated[index].county = '';
    }

    setCounties(updated);
  };

  // Filter students based on search term
  const filteredStudents = students.filter(student => {
    if (!studentSearchTerm) return true;
    const searchLower = studentSearchTerm.toLowerCase();
    return (
      student.name.toLowerCase().includes(searchLower) ||
      student.email.toLowerCase().includes(searchLower) ||
      student.tags?.some(tag => tag.toLowerCase().includes(searchLower))
    );
  });

  // Get available counties for a given state
  const getCountiesForState = (state: string): string[] => {
    if (!state) return [];
    const countyList = Object.values(typedFipsData)
      .filter((record) => record.STATE === state)
      .map((record) => record.COUNTY)
      .sort((a, b) => a.localeCompare(b));
    return [...new Set(countyList)];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!selectedStudent) {
      setError('Please select a student');
      return;
    }

    if (counties.some(c => !c.state || !c.county.trim())) {
      setError('Please fill in all county fields');
      return;
    }

    if (orderType === 'additional_records') {
      if (!additionalRecordsCount || parseInt(additionalRecordsCount) <= 0) {
        setError('Please enter a valid number of additional records');
        return;
      }
      if (!additionalRecordsReason.trim()) {
        setError('Please provide a reason for additional records');
        return;
      }
    }

    try {
      setSubmitting(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token');
      }

      const payload: any = {
        student_user_id: selectedStudent.user_id,
        student_email: selectedStudent.email,
        student_name: selectedStudent.name,
        counties: counties.map(c => ({
          state: c.state,
          county: c.county.trim()
        })),
        order_type: orderType === 'additional_records' ? 'additional_leads' : orderType,
        group_tags: selectedStudent.tags || ['DFPB'], // Include student's tags (backend requires this)
        notes: notes.trim()
      };

      if (orderType === 'additional_records') {
        payload.additional_records_count = parseInt(additionalRecordsCount);
        payload.additional_records_reason = additionalRecordsReason.trim();
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/dfpb-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create order');
      }

      // Success
      onSuccess();
      resetForm();
      onClose();
    } catch (err) {
      console.error('Error creating DFPB order:', err);
      setError(err instanceof Error ? err.message : 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedStudent(null);
    setStudentSearchTerm('');
    setShowStudentDropdown(false);
    setCounties([{ state: '', county: '' }]);
    setOrderType('new_list');
    setAdditionalRecordsCount('');
    setAdditionalRecordsReason('');
    setNotes('');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Create DFPB Order</h3>
          <button
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="p-1 text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
            <p className="text-red-800 font-semibold">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Student Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Select Student <span className="text-red-600">*</span>
            </label>
            <div className="relative student-dropdown-container">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
              <input
                type="text"
                name="student-search-field"
                placeholder="Search by student name, email, or tag (e.g., 'Sarah' or 'DFPB_feb_2025')..."
                value={selectedStudent ? selectedStudent.name : studentSearchTerm}
                onChange={(e) => {
                  setStudentSearchTerm(e.target.value);
                  setSelectedStudent(null);
                  setShowStudentDropdown(true);
                }}
                onFocus={() => setShowStudentDropdown(true)}
                required
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
                data-form-type="other"
                className="w-full pl-12 pr-10 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent"
              />
              {selectedStudent && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedStudent(null);
                    setStudentSearchTerm('');
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              )}

              {/* Dropdown List */}
              {showStudentDropdown && filteredStudents.length > 0 && !selectedStudent && (
                <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-96 overflow-y-auto">
                  {filteredStudents.map((student) => (
                    <button
                      key={student.email}
                      type="button"
                      onClick={() => {
                        setSelectedStudent(student);
                        setStudentSearchTerm('');
                        setShowStudentDropdown(false);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-tdb-accent hover:bg-opacity-10 transition-colors border-b border-gray-100 last:border-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{student.name}</div>
                          <div className="text-sm text-gray-500">{student.email}</div>
                        </div>
                        <div className="flex flex-wrap gap-1 ml-3">
                          {student.tags?.map((tag, i) => (
                            <span
                              key={i}
                              className={`text-xs font-semibold px-2 py-1 rounded ${
                                tag === 'DFPB' ? 'bg-green-100 text-green-800' :
                                tag.startsWith('DFPB_') ? 'bg-teal-100 text-teal-800' :
                                tag.startsWith('BPG_') ? 'bg-orange-100 text-orange-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* No Students Message */}
              {showStudentDropdown && students.length === 0 && !selectedStudent && (
                <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl p-6 text-center">
                  <p className="text-gray-500 font-semibold">No DFPB students available</p>
                  <p className="text-sm text-gray-400 mt-1">Students will appear here once they are tagged with 'DFPB'</p>
                </div>
              )}

              {/* No Matching Students Message */}
              {showStudentDropdown && students.length > 0 && filteredStudents.length === 0 && !selectedStudent && (
                <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl p-6 text-center">
                  <p className="text-gray-500 font-semibold">No students match your search</p>
                  <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
                </div>
              )}
            </div>
          </div>

          {/* Counties (up to 3) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700">
                Counties (Max 3) <span className="text-red-600">*</span>
              </label>
              {counties.length < 3 && (
                <button
                  type="button"
                  onClick={addCounty}
                  className="text-tdb-accent hover:text-[#0ea5ef] font-semibold text-sm flex items-center space-x-1"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add County</span>
                </button>
              )}
            </div>

            <div className="space-y-3">
              {counties.map((county, index) => (
                <div key={index} className="flex items-center space-x-2">
                  {/* State Dropdown */}
                  <select
                    value={county.state}
                    onChange={(e) => updateCounty(index, 'state', e.target.value)}
                    required
                    className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent"
                  >
                    <option value="">State</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>

                  {/* County Dropdown */}
                  <select
                    value={county.county}
                    onChange={(e) => updateCounty(index, 'county', e.target.value)}
                    required
                    disabled={!county.state}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select County</option>
                    {getCountiesForState(county.state).map(countyName => (
                      <option key={countyName} value={countyName}>{countyName}</option>
                    ))}
                  </select>

                  {/* Remove Button */}
                  {counties.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCounty(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Order Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Order Type <span className="text-red-600">*</span>
            </label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 p-3 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="orderType"
                  value="new_list"
                  checked={orderType === 'new_list'}
                  onChange={(e) => setOrderType(e.target.value as 'new_list')}
                  data-lpignore="true"
                  className="w-4 h-4 text-tdb-accent focus:ring-tdb-accent"
                />
                <div>
                  <div className="font-semibold text-gray-900">New List</div>
                  <div className="text-sm text-gray-500">Full county tax delinquent list</div>
                </div>
              </label>

              <label className="flex items-center space-x-3 p-3 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="orderType"
                  value="additional_records"
                  checked={orderType === 'additional_records'}
                  onChange={(e) => setOrderType(e.target.value as 'additional_records')}
                  data-lpignore="true"
                  className="w-4 h-4 text-tdb-accent focus:ring-tdb-accent"
                />
                <div>
                  <div className="font-semibold text-gray-900">Additional Records</div>
                  <div className="text-sm text-gray-500">Replacement records for bad data</div>
                </div>
              </label>
            </div>
          </div>

          {/* Additional Records Fields */}
          {orderType === 'additional_records' && (
            <div className="space-y-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Number of Records <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  value={additionalRecordsCount}
                  onChange={(e) => setAdditionalRecordsCount(e.target.value)}
                  placeholder="e.g., 50"
                  min="1"
                  required={orderType === 'additional_records'}
                  data-lpignore="true"
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason <span className="text-red-600">*</span>
                </label>
                <select
                  value={additionalRecordsReason}
                  onChange={(e) => setAdditionalRecordsReason(e.target.value)}
                  required={orderType === 'additional_records'}
                  data-lpignore="true"
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent"
                >
                  <option value="">Select reason...</option>
                  <option value="vacant_properties">Vacant properties - our fault</option>
                  <option value="land_only">Land-only parcels - data issue</option>
                  <option value="bad_data_charge">Bad data - charge student</option>
                  <option value="quality_refund">Quality issue - refund</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Internal Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add any internal notes about this order..."
              data-lpignore="true"
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-tdb-accent text-white rounded-lg hover:bg-[#0ea5ef] font-semibold disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
